import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { AppError } from '../../utils/app-error';
import { reportError } from '../../utils/report-error';

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
        this.reset = this.reset.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        const appError = error instanceof AppError ? error : AppError.from(error);
        // Update state so the fallback shows the same correlationId we report.
        if (appError !== this.state.error) {
            this.setState({ error: appError });
        }
        reportError(appError, {
            boundary: this.props.name || 'unnamed',
            componentStack: info?.componentStack,
        });
    }

    reset() {
        this.setState({ hasError: false, error: null });
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }
        if (typeof this.props.fallback === 'function') {
            return this.props.fallback({ error: this.state.error, reset: this.reset });
        }
        const correlationId = this.state.error?.correlationId ?? 'unknown';
        return (
            <div className="ErrorBoundary" role="alert">
                <h2>Something went wrong</h2>
                <p>An unexpected error occurred. You can try again, or contact support with the reference id below.</p>
                <p>
                    <strong>Reference id:</strong> <code>{correlationId}</code>
                </p>
                <button type="button" onClick={this.reset}>Try again</button>
            </div>
        );
    }
}

ErrorBoundary.propTypes = {
    name: PropTypes.string,
    children: PropTypes.node,
    fallback: PropTypes.func,
};

export default ErrorBoundary;
